// @flow
import React, { useEffect } from "react";
import Routes from "./routes/Routes";
import ErrorBoundary from "./components/ErrorBoundary";
import i18n from "./i18n";

// setup fake backend
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Themes
import { VERSION } from "./constants/version";
// For Saas import Saas.scss
import "./assets/scss/Saas.scss";
// For Dark import Saas-Dark.scss
// import './assets/scss/Saas-Dark.scss';

// For Modern demo import Modern.scss
// import './assets/scss/Modern.scss';
// For Modern dark demo import Modern-Dark.scss
// import './assets/scss/Modern-Dark.scss';

// For Creative demo import Creative.scss
// import './assets/scss/Creative.scss';
// For Creative dark demo import Creative-Dark.scss
// import './assets/scss/Creative-Dark.scss';

// configure fake backend
import PermissionProvider from "./helpers/permission/PermissionProvider.js";
import { ProfileImageProvider } from "./helpers/ProfileImageProvider.js";
import { useSelector, useDispatch } from "react-redux";
import { updateUser } from "./redux/actions";
import { getCurrentUser } from "aws-amplify/auth";
import CookieBanner from "./components/CookieBanner/CookieBanner.js";
import { USER_STORAGE_KEYS } from "./constants/storage";
import { refreshUserPermissions } from "./helpers/api/auth";
import { APICore } from "./helpers/api/apiCore";
import { HideDataProvider } from "./helpers/HideDataProvider.js";
import { useHideDataShortcut } from "./hooks/useHideDataShortcut";

//actions

const api = new APICore();

// The Shift + H shortcut reads Privacy Mode through useHideData, so it sits INSIDE
// HideDataProvider — mounting it in App itself would hand it the empty default context.
const PrivacyModeEffects = () => {
  useHideDataShortcut();
  return null;
};

/**
 * Main app component
 */
const App = (props) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => ({
    user: state.Auth.user,
  }));

  useEffect(() => {
    const storedUser = localStorage.getItem(USER_STORAGE_KEYS.AUTH_SESSION);
    if (storedUser) {
      dispatch(updateUser(JSON.parse(storedUser)));
    }
  }, [dispatch]);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      document.documentElement.lang = lng;
    };

    handleLanguageChanged(i18n.language);
    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  // Refresh the logged-in user's role permissions and settings (quick filters,
  // etc.) in place, once per mount. It never logs the user out. Runs
  // unconditionally (not just on a version mismatch) so data changed on another
  // device — e.g. a quick filter added on mobile — shows up on refresh rather
  // than requiring re-login. See plan/version.js.
  useEffect(() => {
    const storedUser = api.getLoggedInUser();
    if (!storedUser || !storedUser.id) return; // logged out / auth pages -> no-op

    let cancelled = false;
    (async () => {
      try {
        const fresh = await refreshUserPermissions(storedUser.id);
        if (cancelled) return;
        // Re-read rather than reusing the mount-time snapshot: this call is asynchronous, and
        // anything written while it was in flight lives in the local copy by now.
        const current = api.getLoggedInUser() || storedUser;
        const merged = { ...current, ...fresh, version: VERSION };
        api.setLoggedInUserInLocalStorage(merged); // survives reloads; shared across tabs
        dispatch(updateUser(merged)); // updates Redux + UI permission gates live
      } catch (err) {
        // Never log the user out here — keep the session; it retries on the next load.
        console.error("Failed to refresh user permissions", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (!user && !window.location.href.includes("http://localhost:3000")) ||
      (user &&
        user.role !== "developer" &&
        !window.location.href.includes("http://localhost:3000"))
    ) {
      // eslint-disable-next-line
      console = {};
      console.log = function () {};
      console.info = function () {};
      console.warn = function () {};
      console.error = function () {};
    }
  }, [user]);

  // Automatically logout user if token is expired or invalid

  const currentAuthenticatedUser = async () => {
    try {
      await getCurrentUser();
    } catch (err) {
      if (
        !window.location.href.includes("/logout") &&
        !window.location.href.includes("/login") &&
        !window.location.href.includes("/register") &&
        !window.location.href.includes("/forget-password") &&
        !window.location.href.includes("/confirm") &&
        !window.location.href.includes("/error-404") &&
        !window.location.href.includes("/error-500") &&
        !window.location.href.includes("/new-password") &&
        !window.location.href.includes("/verify-email") &&
        // Public offer pages: customers accept via email link without a login.
        !window.location.href.includes("/offer/public/") &&
        !window.location.href.includes("/offer/accepted")
      ) {
        window.location.replace("/logout");
      }
    }
  };
  useEffect(() => {
    currentAuthenticatedUser();
  }, []);

  return (
    <ErrorBoundary>
      <HideDataProvider>
        <PermissionProvider>
          <ProfileImageProvider>
            <PrivacyModeEffects />
            <ToastContainer />
            <Routes></Routes>
            <CookieBanner />
          </ProfileImageProvider>
        </PermissionProvider>
      </HideDataProvider>
    </ErrorBoundary>
  );
};
export default App;
